import { useCallback, useMemo } from 'react';
import _ from 'lodash';

import {
  useAddArticle,
  useAllCategories,
  useArticle,
  useDeleteArticle,
  useEditArticle,
  useEditArticlePublicationDate,
  useFetchArticles,
  useFileUrl,
  useTags
} from 'api/document';
import { Article, LovFileCategory } from 'types/Document';

interface ArticleCategory {
  id: number;
  name: string;
  child_categories: Pick<LovFileCategory, 'id' | 'name'>[];
}

export const useArticles = () => {
  const {
    data: articles,
    isLoading: isArticlesLoading,
    isInitialLoading,
    isError: isArticlesError
  } = useFetchArticles();
  const { data: allCategories } = useAllCategories();

  const { data: tags } = useTags();

  const articleCategories = useMemo(() => {
    if (!articles) return [];

    const allArticleCategories = _.uniqBy(
      articles
        .map((article: Article) => {
          let category = article.lovFileCategory;
          const categories: LovFileCategory[] = [];
          while (category) {
            categories.push(category);
            category = category.lovFileCategory as LovFileCategory;
          }
          return categories;
        })
        .flat(),
      'id'
    );
    const allArticleCategoriesIds = allArticleCategories.map((category) => category.id);

    const primaryCategories = allCategories?.filter((category) => allArticleCategoriesIds.includes(category.id));

    const uniqueNestedCategories = primaryCategories?.map((category) => {
      const { otherLovFileCategory, ...rest } = category;
      let subCategories = otherLovFileCategory;

      if (!subCategories?.length) return { ...category };
      subCategories = subCategories.filter((subCategory) => allArticleCategoriesIds.includes(subCategory.id));
      return {
        ...rest,
        child_categories: subCategories
      };
    });

    return uniqueNestedCategories as ArticleCategory[];
  }, [articles, allCategories]);

  const getArticlesByCategory = useCallback(
    (categoryId: number): Article[] => {
      if (!articles) return [];

      return articles.filter((article: { lovFileCategory: any }) => {
        let articleCategory = article.lovFileCategory;
        // get articles matching any level of category
        while (articleCategory?.lovFileCategory) {
          articleCategory = articleCategory.lovFileCategory;
          if (articleCategory?.id === categoryId) return true;
        }

        return articleCategory?.id === categoryId || categoryId == article.lovFileCategory?.id;
      });
    },
    [articles]
  );

  const getArticleCategory = useCallback((article: Article): LovFileCategory => {
    //   return innermost category
    let category = article.lovFileCategory;
    while (category?.lovFileCategory) {
      category = category.lovFileCategory;
    }
    return category;
  }, []);

  const returnValue = useMemo(() => {
    return {
      articlesData: {
        articles,
        isArticlesLoading: isArticlesLoading || isInitialLoading,
        isArticlesError,
        articleCategories
      },
      allCategories,
      tags,
      getArticlesByCategory,
      getArticleCategory,
      useArticle,
      useAddArticle,
      useEditArticle,
      useEditArticlePublicationDate,
      useDeleteArticle,
      useFileUrl
    };
  }, [
    articles,
    isArticlesLoading,
    isInitialLoading,
    isArticlesError,
    articleCategories,
    allCategories,
    tags,
    getArticlesByCategory,
    getArticleCategory
  ]);

  return returnValue;
};
